import { useEffect, useState } from 'react';
import {
  api,
  type ApiKey,
  type CalendarConnection,
  type CalendarSyncResult,
  type Child,
  type Invite,
  type Member,
  type MeResponse,
  type PurgePreview,
} from '../api';
import { messageFor } from '../App';
import { UnverifiedNotice } from './UnverifiedNotice';

/** Children, people, API keys, and the spend cap. */
export function SettingsView({ me, onChanged }: { me: MeResponse; onChanged: () => void }) {
  const [error, setError] = useState<string>();
  const [childName, setChildName] = useState('');
  const [grade, setGrade] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [purging, setPurging] = useState<{ child: Child; preview: PurgePreview } | null>(null);
  const [calendars, setCalendars] = useState<CalendarConnection[]>([]);
  const [syncResult, setSyncResult] = useState<CalendarSyncResult | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invites, setInvites] = useState<Invite[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [inviteRole, setInviteRole] = useState<Member['role']>('owner');
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [keyName, setKeyName] = useState('');
  const [newSecret, setNewSecret] = useState<string>();
  const [busy, setBusy] = useState(false);

  const isOwner = me.user?.role === 'owner';

  useEffect(() => {
    if (!isOwner) return;
    void api.apiKeys().then((res) => setKeys(res.keys)).catch((err) => setError(messageFor(err)));
    void api.invites().then((res) => setInvites(res.invites)).catch((err) => setError(messageFor(err)));
    void api.members().then((res) => setMembers(res.members)).catch((err) => setError(messageFor(err)));
    void api.calendar().then((res) => setCalendars(res.connections)).catch(() => undefined);
  }, [isOwner]);

  async function guard(action: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    try {
      await action();
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  const usage = me.usage;

  return (
    <>
      {error && <div className="notice error">{error}</div>}

      {purging && (
        <PurgeDialog
          child={purging.child}
          preview={purging.preview}
          onClose={() => setPurging(null)}
          onDone={() => {
            setPurging(null);
            onChanged();
          }}
        />
      )}

      <div className="notice info">
        Everything on this page can also be changed by talking to the Assistant, including by
        voice — “Ada started third grade”, “Linus is measured under Missouri too”, “we moved
        to Kansas City so change the time zone”. Useful when your hands are full.
      </div>

      <div className="panel">
        <h2>Students</h2>
        <p className="sub">
          Everything here is editable — click a field and change it. Give each child a
          distinct name: the assistant resolves spoken names to these, and two children
          called the same thing cannot be told apart. Each student is measured against their
          own requirement, so siblings can sit under different rules.
        </p>
        <table>
          <thead>
            <tr><th>Name</th><th>Grade</th><th>Born</th><th>Measured against</th><th /></tr>
          </thead>
          <tbody>
            {(me.children ?? []).map((child) => (
              <tr key={child.childId}>
                <td>
                  <span className="swatch" style={{ background: child.color, borderColor: child.color }} />{' '}
                  <input
                    className="inline-edit"
                    defaultValue={child.name}
                    aria-label={`Name for ${child.name}`}
                    onBlur={(e) => {
                      const name = e.target.value.trim();
                      if (!name || name === child.name) {
                        e.target.value = child.name;
                        return;
                      }
                      void guard(async () => {
                        await api.updateChild(child.childId, { name });
                        onChanged();
                      });
                    }}
                  />
                </td>
                <td>
                  <input
                    className="inline-edit narrow-edit"
                    placeholder="grade"
                    defaultValue={child.gradeLevel ?? ''}
                    aria-label={`Grade for ${child.name}`}
                    onBlur={(e) => {
                      const gradeLevel = e.target.value.trim() || null;
                      if (gradeLevel === (child.gradeLevel ?? null)) return;
                      void guard(async () => {
                        await api.updateChild(child.childId, { gradeLevel });
                        onChanged();
                      });
                    }}
                  />
                </td>
                <td>
                  <input
                    className="inline-edit"
                    type="date"
                    defaultValue={child.birthDate ?? ''}
                    aria-label={`Birth date for ${child.name}`}
                    onBlur={(e) => {
                      const birthDate = e.target.value || null;
                      if (birthDate === (child.birthDate ?? null)) return;
                      void guard(async () => {
                        await api.updateChild(child.childId, { birthDate });
                        onChanged();
                      });
                    }}
                  />
                </td>
                <td>
                  <select
                    value={child.jurisdictionId}
                    onChange={(e) =>
                      void guard(async () => {
                        await api.updateChild(child.childId, { jurisdictionId: e.target.value });
                        onChanged();
                      })
                    }
                  >
                    {(me.jurisdictions ?? []).map((j) => (
                      <option key={j.id} value={j.id}>{j.label}</option>
                    ))}
                  </select>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {child.active && (
                    <button
                      title="Stops them appearing day to day. Their history is untouched."
                      onClick={() =>
                        void guard(async () => {
                          await api.updateChild(child.childId, { active: false });
                          onChanged();
                        })
                      }
                    >
                      Retire
                    </button>
                  )}{' '}
                  <button
                    className="danger"
                    title="For a student who should never have been added at all"
                    onClick={() =>
                      void guard(async () => {
                        const preview = (await api.previewChildPurge(child.childId)).preview;
                        setPurging({ child, preview });
                      })
                    }
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {(me.children ?? []).length === 0 && (
              <tr><td colSpan={5} className="muted">No students yet.</td></tr>
            )}
          </tbody>
        </table>

        <div className="row" style={{ marginTop: 14 }}>
          <div>
            <label htmlFor="cname">Name</label>
            <input id="cname" value={childName} onChange={(e) => setChildName(e.target.value)} />
          </div>
          <div>
            <label htmlFor="cgrade">Grade (optional)</label>
            <input id="cgrade" placeholder="2nd" value={grade} onChange={(e) => setGrade(e.target.value)} />
          </div>
          <div>
            <label htmlFor="cdob">Born (optional)</label>
            <input id="cdob" type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
          </div>
          <div className="narrow">
            <button
              className="primary"
              disabled={busy || !childName.trim()}
              onClick={() =>
                void guard(async () => {
                  await api.createChild({
                    name: childName.trim(),
                    ...(grade.trim() ? { gradeLevel: grade.trim() } : {}),
                    ...(birthDate ? { birthDate } : {}),
                  });
                  setChildName('');
                  setGrade('');
                  setBirthDate('');
                  onChanged();
                })
              }
            >
              Add student
            </button>
          </div>
        </div>
      </div>

      {isOwner && me.account && (
        <div className="panel">
          <h2>Family</h2>
          <p className="sub">
            Changing the time zone does not move entries that are already recorded — they keep
            the local times they were logged with.
          </p>
          <div className="row">
            <div>
              <label htmlFor="fname">Account name</label>
              <input
                id="fname"
                defaultValue={me.account.name}
                onBlur={(e) =>
                  e.target.value.trim() &&
                  e.target.value !== me.account?.name &&
                  void guard(async () => {
                    await api.updateAccount({ name: e.target.value.trim() });
                    onChanged();
                  })
                }
              />
            </div>
            <div>
              <label htmlFor="ftz">Time zone</label>
              <input
                id="ftz"
                defaultValue={me.account.timeZone}
                onBlur={(e) =>
                  e.target.value.trim() &&
                  e.target.value !== me.account?.timeZone &&
                  void guard(async () => {
                    await api.updateAccount({ timeZone: e.target.value.trim() });
                    onChanged();
                  })
                }
              />
            </div>
            <div>
              <label htmlFor="fjur">Default for new students</label>
              <select
                id="fjur"
                value={me.account.jurisdictionId}
                onChange={(e) =>
                  void guard(async () => {
                    await api.updateAccount({ jurisdictionId: e.target.value });
                    onChanged();
                  })
                }
              >
                {(me.jurisdictions ?? []).map((j) => (
                  <option key={j.id} value={j.id}>{j.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {usage && (
        <div className="panel">
          <h2>AI spending this month</h2>
          <p className="sub">
            Transcription and summarization are metered per account and stop hard at the cap.
            Editing the log by hand is never blocked.
          </p>
          <div className="progress">
            <div>
              <div className="label">
                <span>{usage.month}</span>
                <span className="muted">
                  ${(usage.spentCents / 100).toFixed(2)} of ${(usage.capCents / 100).toFixed(2)}
                </span>
              </div>
              <div className="bar">
                <span style={{ width: `${Math.min(100, (usage.spentCents / Math.max(1, usage.capCents)) * 100)}%` }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {isOwner && (
        <div className="panel">
          <h2>People</h2>
          <p className="sub">
            Everyone who can reach this family's records. Invite by email — there is no link
            to send and no code to pass along; they sign in with that Google account and they
            are in.
          </p>

          <table>
            <thead>
              <tr><th>Email</th><th>Role</th><th>Last seen</th><th /></tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.userId}>
                  <td>
                    {member.email}
                    {member.isYou && <span className="muted"> (you)</span>}
                  </td>
                  <td>
                    <select
                      value={member.role}
                      onChange={(e) =>
                        void guard(async () => {
                          await api.updateMember(member.userId, e.target.value as Member['role']);
                          setMembers((await api.members()).members);
                          onChanged();
                        })
                      }
                    >
                      <option value="owner">Owner — can manage everything</option>
                      <option value="parent">Parent — can log and edit</option>
                      <option value="viewer">Viewer — read only</option>
                    </select>
                  </td>
                  <td className="muted">{member.lastSeenAt?.slice(0, 10) ?? 'never'}</td>
                  <td>
                    <button
                      className="danger"
                      title="Removes their access. Everything they logged stays."
                      onClick={() =>
                        void guard(async () => {
                          if (
                            !window.confirm(
                              `Remove ${member.email}? They lose access immediately. ` +
                                `Everything they logged stays in the record.`,
                            )
                          ) {
                            return;
                          }
                          await api.removeMember(member.userId);
                          setMembers((await api.members()).members);
                        })
                      }
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="muted" style={{ marginTop: 10 }}>
            Removing someone revokes access but keeps their history — their name stays on the
            entries they logged, which is what makes an export hold up.
          </p>

          {invites.filter((i) => !i.acceptedAt).length > 0 && (
            <>
              <h2 style={{ marginTop: 20, fontSize: 14 }}>Invited, not yet joined</h2>
              <table>
                <tbody>
                  {invites
                    .filter((invite) => !invite.acceptedAt)
                    .map((invite) => (
                      <tr key={invite.email}>
                        <td>{invite.email}</td>
                        <td className="muted">{invite.role}</td>
                        <td className="muted">
                          {new Date(invite.expiresAt) < new Date()
                            ? `expired ${invite.expiresAt.slice(0, 10)}`
                            : `expires ${invite.expiresAt.slice(0, 10)}`}
                        </td>
                        <td>
                          <button
                            className="danger"
                            onClick={() =>
                              void guard(async () => {
                                await api.revokeInvite(invite.email);
                                setInvites((await api.invites()).invites);
                              })
                            }
                          >
                            Withdraw
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </>
          )}

          <div className="row" style={{ marginTop: 14 }}>
            <div>
              <label htmlFor="iemail">Email address</label>
              <input
                id="iemail"
                type="email"
                placeholder="wife@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="irole">Role</label>
              <select
                id="irole"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as Member['role'])}
              >
                <option value="owner">Owner — can manage everything</option>
                <option value="parent">Parent — can log and edit</option>
                <option value="viewer">Viewer — read only</option>
              </select>
            </div>
            <div className="narrow">
              <button
                disabled={busy || !inviteEmail.trim()}
                onClick={() =>
                  void guard(async () => {
                    await api.createInvite({ email: inviteEmail.trim(), role: inviteRole });
                    setInviteEmail('');
                    setInvites((await api.invites()).invites);
                  })
                }
              >
                Invite
              </button>
            </div>
          </div>
          <p className="muted" style={{ marginTop: 8 }}>
            Check the address before sending. Whoever controls that mailbox will be able to
            see and edit this family's records.
          </p>
        </div>
      )}

      <div className="panel">
        <h2>Google Calendar</h2>
        <p className="sub">
          Entries that look like instruction are brought in unconfirmed, attributed to every
          student, for you to narrow and accept. Times come from the calendar entry; only the
          subject and description are worked out. Read-only — nothing is ever written back.
        </p>

        {new URLSearchParams(window.location.search).get('calendar') === 'no_refresh_token' && (
          <div className="notice warn">
            Google did not return a lasting grant, which happens if this app was already
            authorised. Remove it under your Google account's third-party access, then
            connect again.
          </div>
        )}

        {calendars.length === 0 ? (
          <>
            <p className="muted">No calendar connected.</p>
            <UnverifiedNotice />
            <button
              className="primary"
              disabled={busy}
              onClick={() =>
                void guard(async () => {
                  const { url } = await api.connectCalendar();
                  window.location.assign(url);
                })
              }
            >
              Connect Google Calendar
            </button>
          </>
        ) : (
          <>
            <table>
              <tbody>
                {calendars.map((connection) => (
                  <tr key={connection.userId}>
                    <td>{connection.email}</td>
                    <td className="muted">
                      {connection.lastSyncAt
                        ? `last checked ${new Date(connection.lastSyncAt).toLocaleString()}`
                        : 'not checked yet'}
                    </td>
                    <td>
                      {connection.isYou && (
                        <button
                          className="danger"
                          onClick={() =>
                            void guard(async () => {
                              await api.disconnectCalendar();
                              setCalendars((await api.calendar()).connections);
                            })
                          }
                        >
                          Disconnect
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {syncResult && (
              <div className="notice info">
                Checked {syncResult.considered} entries from the last four weeks:{' '}
                {syncResult.imported} brought in, {syncResult.skipped} left alone.
                {syncResult.imported > 0 && ' They are on the calendar, waiting to be confirmed.'}
              </div>
            )}

            <button
              disabled={busy}
              style={{ marginTop: 10 }}
              onClick={() =>
                void guard(async () => {
                  setSyncResult((await api.syncCalendar()).result);
                  onChanged();
                })
              }
            >
              {busy ? 'Checking…' : 'Check for new entries'}
            </button>
            <p className="muted" style={{ marginTop: 8 }}>
              Disconnecting stops future checks. Entries already brought in stay — they are
              part of the log now.
            </p>
          </>
        )}
      </div>

      {isOwner && (
        <div className="panel">
          <h2>API keys</h2>
          <p className="sub">
            For the MCP endpoint at <span className="mono">/mcp</span>, so an external agent can
            read and adjust this log. Keys are stored as hashes and shown only once.
          </p>

          {newSecret && (
            <div className="notice info">
              <p style={{ margin: '0 0 6px' }}>
                Copy this now — it cannot be shown again.
              </p>
              <p className="mono" style={{ margin: 0 }}>{newSecret}</p>
            </div>
          )}

          <table>
            <thead>
              <tr><th>Name</th><th>Role</th><th>Last used</th><th /></tr>
            </thead>
            <tbody>
              {keys.filter((key) => !key.revokedAt).map((key) => (
                <tr key={key.keyId}>
                  <td>{key.name}</td>
                  <td className="muted">{key.role}</td>
                  <td className="muted">{key.lastUsedAt ?? 'never'}</td>
                  <td>
                    <button
                      className="danger"
                      onClick={() =>
                        void guard(async () => {
                          await api.revokeApiKey(key.keyId);
                          setKeys((await api.apiKeys()).keys);
                        })
                      }
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
              {keys.filter((key) => !key.revokedAt).length === 0 && (
                <tr><td colSpan={4} className="muted">No active keys.</td></tr>
              )}
            </tbody>
          </table>

          <div className="row" style={{ marginTop: 12 }}>
            <div>
              <label htmlFor="kname">Name this key</label>
              <input
                id="kname"
                placeholder="Claude Desktop"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
              />
            </div>
            <div className="narrow">
              <button
                disabled={busy || !keyName.trim()}
                onClick={() =>
                  void guard(async () => {
                    const res = await api.createApiKey(keyName.trim());
                    setNewSecret(res.secret);
                    setKeyName('');
                    setKeys((await api.apiKeys()).keys);
                  })
                }
              >
                Create key
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Removing a student who should never have been recorded.
 *
 * Deliberately harder than retiring: this is the one action whose effect eventually cannot
 * be undone, so it states plainly what will happen to their entries, distinguishes those
 * shared with a sibling from those that are not, and asks for the name to be typed. The
 * server checks the name too — a confirmation only the browser enforces is decoration.
 */
function PurgeDialog({
  child,
  preview,
  onClose,
  onDone,
}: {
  child: Child;
  preview: PurgePreview;
  onClose: () => void;
  onDone: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const matches = typed.trim().toLowerCase() === child.name.trim().toLowerCase();

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="presentation"
    >
      <div className="modal" role="dialog" aria-modal="true">
        <h2>Delete {child.name}?</h2>
        <p className="sub">
          This is for a student who should never have been added — a test, or a duplicate.
          To stop someone appearing day to day while keeping their record, retire them
          instead.
        </p>

        {error && <div className="notice error">{error}</div>}

        <div className="notice warn">
          <strong>What this does</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            <li>
              {preview.eventsArchived} entr{preview.eventsArchived === 1 ? 'y' : 'ies'} recorded
              only for {child.name} will be removed from the log and from every export.
            </li>
            {preview.eventsAmended > 0 && (
              <li>
                {preview.eventsAmended} entr{preview.eventsAmended === 1 ? 'y' : 'ies'} shared
                with another student will be kept, with {child.name} removed from them.
              </li>
            )}
            <li>Past years and progress for {child.name} will be removed.</li>
            <li>
              Recoverable for 90 days by whoever operates this service, then genuinely gone.
            </li>
          </ul>
        </div>

        <div className="field" style={{ marginTop: 12 }}>
          <label htmlFor="purge-confirm">Type <strong>{child.name}</strong> to confirm</label>
          <input
            id="purge-confirm"
            value={typed}
            autoFocus
            onChange={(e) => setTyped(e.target.value)}
          />
        </div>

        <div className="modal-actions">
          <button
            className="danger"
            disabled={!matches || busy}
            onClick={async () => {
              setBusy(true);
              setError(undefined);
              try {
                await api.purgeChild(child.childId, typed.trim());
                onDone();
              } catch (err) {
                setError(messageFor(err));
                setBusy(false);
              }
            }}
          >
            {busy ? 'Removing…' : `Delete ${child.name}`}
          </button>
          <span className="spacer" />
          <button disabled={busy} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
